import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  MenuItem,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { getMetalsRequest } from '../../../redux/GetMetals/metalSlice';
import { createOrUpdateOfferRequest } from '../../../redux/settings/settingsSlice';
import ReferralOrdersTable from '../../Settings/ReferralOrdersTable';
import AccessControl from '../../Comman/AccessControl';
import { controlAllFeatureOptions } from '../../../configs/constants';
import { canShowPermission } from '../Permissions/permissionObject';

const ReferralBenefits = () => {
  const dispatch = useDispatch();
  const metalState = useSelector((state) => state.metal || {});
  const enabledMetals = metalState.data?.metals || [];

  const initialFormData = {
    customerType: '',
    metal: '',
    offerType: '',
    referralType: '',
    transactionsFrequency: '',
    transactionsType: '',
    percentage: '',
    points: '',
    joinerBullion: '',
    joinerPointsPerGram: '',
    pointsPerGram: '',
    startDate: null,
    endDate: null,
    validityDays: '',
    buyMetal: '',
    isDiamondPoint: false,
    diamondPointType: '',
    diamondPointValue: '',
  };

  const [formData1, setFormData1] = useState(initialFormData);
  const [loadingBenfits, setLoadingBenfits] = useState(false);

  const [permissions, setPermissions] = useState({});
  useEffect(() => {
    const storedPermissions = localStorage.getItem('userPermissions');
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (error) {
        console.error('Error parsing stored permissions', error);
      }
    }
  }, []);

  const canCreate = canShowPermission(
    permissions,
    'referralBenefits',
    'create',
  );
  const canEdit = canShowPermission(permissions, 'referralBenefits', 'edit');
  const canDelete = canShowPermission(
    permissions,
    'referralBenefits',
    'delete',
  );

  useEffect(() => {
    dispatch(getMetalsRequest());
  }, [dispatch]);

  const handleChange = (field, value) => {
    setFormData1((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit1 = () => {
    if (!formData1.referralType || !formData1.transactionsFrequency) {
      toast.error(
        'Please fill referralType and transactionsFrequency required fields!',
      );
      return;
    }

    if (!formData1.startDate || !formData1.endDate) {
      toast.error('Please select both start and end dates!');
      return;
    }

    if (!formData1.validityDays) {
      toast.error('Please enter validity days!');
      return;
    }

    if (Number(formData1.validityDays) <= 0) {
      toast.error('Validity days must be greater than 0!');
      return;
    }

    if (dayjs(formData1.startDate).isAfter(dayjs(formData1.endDate))) {
      toast.error('Start date must be earlier than end date!');
      return;
    }

    if (
      formData1.joinerPointsPerGram !== '' &&
      formData1.joinerPointsPerGram !== null &&
      Number(formData1.joinerPointsPerGram) < 0
    ) {
      toast.error('Joiner Points Per Gram cannot be negative!');
      return;
    }
    if (
      formData1.referralType === 'CustomerToCustomer' &&
      formData1.transactionsFrequency !== 'REGISTRATION_GIFT' &&
      !formData1.buyMetal
    ) {
      toast.error('Please select Buy Metal!');
      return;
    }

    if (formData1.isDiamondPoint) {
      if (!formData1.diamondPointType) {
        toast.error('Please select Diamond Point Type (Fixed/Percentage)!');
        return;
      }
      if (
        formData1.diamondPointValue === '' ||
        Number(formData1.diamondPointValue) <= 0
      ) {
        toast.error('Please enter a valid Diamond Point value!');
        return;
      }
      if (
        formData1.diamondPointType === 'PERCENTAGE' &&
        Number(formData1.diamondPointValue) > 100
      ) {
        toast.error('Diamond Point percentage cannot exceed 100!');
        return;
      }
    }
    const payload = {
      referralType: formData1.referralType?.trim(),
      bullion: formData1.metal?.trim(),
      buyMetal: formData1.buyMetal?.trim() || null,
      joinerBullion: formData1.joinerBullion?.trim(),
      transactionsFrequency: formData1.transactionsFrequency?.trim(),
      transactionsType: formData1.transactionsType?.trim(),
      percentage: formData1.percentage ? Number(formData1.percentage) : null,
      joinerPointsPerGram: formData1.joinerPointsPerGram
        ? Number(formData1.joinerPointsPerGram)
        : null,
      points: formData1.points ? Number(formData1.points) : 0,
      pointsPerGram: formData1.pointsPerGram
        ? Number(formData1.pointsPerGram)
        : 0,
      startDate: formData1.startDate
        ? dayjs(formData1.startDate).valueOf()
        : null,
      endDate: formData1.endDate ? dayjs(formData1.endDate).valueOf() : null,
      validityDays: formData1.validityDays
        ? Number(formData1.validityDays)
        : null,
      isDiamondPoint: !!formData1.isDiamondPoint,
      diamondPointType: formData1.isDiamondPoint
        ? formData1.diamondPointType
        : null,
      diamondPointValue: formData1.isDiamondPoint
        ? Number(formData1.diamondPointValue)
        : null,
    };

    setLoadingBenfits(true);

    setTimeout(() => {
      dispatch(createOrUpdateOfferRequest(payload));
      setLoadingBenfits(false);
      setFormData1(initialFormData);
    }, 1000);
  };

  return (
    <AccessControl feature={controlAllFeatureOptions.Referral_Benefit}>
      <Box sx={{ width: '100%', mt: 0, mb: { xs: 2, md: 2 } }}>
        <Paper
          elevation={2}
          sx={{
            p: 3,
            width: '100%',
            border: '1px solid #ccc',
          }}
        >
          <Typography
            fontWeight={700}
            fontSize={24}
            mb={1}
            fontFamily={'Aeonik'}
          >
            Referral Benefits
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Referral Type"
                fullWidth
                size="small"
                value={formData1.referralType || ''}
                onChange={(e) => handleChange('referralType', e.target.value)}
                InputLabelProps={{
                  shrink: !!formData1.referralType,
                }}
                sx={{ minWidth: 200 }}
                disabled={!canEdit}
              >
                <MenuItem value="CustomerToCustomer">
                  Customer to Customer
                </MenuItem>
                <MenuItem value="UserToCustomer">User to Customer</MenuItem>
              </TextField>
            </Grid>

             <Grid item xs={12} md={6}>
              <TextField
                select
                label="Transaction Frequency"
                fullWidth
                size="small"
                value={formData1.transactionsFrequency || ''}
                onChange={(e) =>
                  handleChange('transactionsFrequency', e.target.value)
                }
                InputLabelProps={{
                  shrink: !!formData1.transactionsFrequency,
                }}
                sx={{ minWidth: 210 }}
                disabled={!canEdit}
              >
                <MenuItem value="REGISTRATION_GIFT">REGISTRATION_GIFT</MenuItem>
                <MenuItem value="FIRST_TRANSACTION">FIRST_TRANSACTION</MenuItem>
                <MenuItem value="EVERY_TRANSACTION">EVERY_TRANSACTION</MenuItem>
              </TextField>
            </Grid>

            {/* Buy Metal — condition, only for FIRST/EVERY transaction, not for REGISTRATION_GIFT */}
            {formData1.referralType === 'CustomerToCustomer' &&
              formData1.transactionsFrequency &&
              formData1.transactionsFrequency !== 'REGISTRATION_GIFT' && (
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Buy Metal"
                    fullWidth
                    size="small"
                    required
                    value={formData1.buyMetal || ''}
                    onChange={(e) => handleChange('buyMetal', e.target.value)}
                    InputLabelProps={{ shrink: !!formData1.buyMetal }}
                    sx={{ minWidth: 200 }}
                    disabled={!canEdit}
                  >
                    {enabledMetals
                      .filter((m) => m.status === true)
                      .map((m) => (
                        <MenuItem key={m._id} value={m._id}>
                          {`${m.metalName} - ${m.karat}K - ${m.purity}`}
                        </MenuItem>
                      ))}
                  </TextField>
                </Grid>
              )}

            {/* Reward selection: metal dropdown OR Diamond Point (FIRST/EVERY only, per current design) */}
            {formData1.referralType === 'CustomerToCustomer' &&
              formData1.transactionsFrequency &&
              formData1.transactionsFrequency !== 'REGISTRATION_GIFT' && (
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Reward"
                    fullWidth
                    size="small"
                    required
                    value={
                      formData1.isDiamondPoint
                        ? 'DIAMOND_POINT'
                        : formData1.metal || ''
                    }
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === 'DIAMOND_POINT') {
                        setFormData1((prev) => ({
                          ...prev,
                          isDiamondPoint: true,
                          metal: '',
                          percentage: '',
                        }));
                      } else {
                        setFormData1((prev) => ({
                          ...prev,
                          isDiamondPoint: false,
                          metal: val,
                          diamondPointType: '',
                          diamondPointValue: '',
                        }));
                      }
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ minWidth: 200 }}
                    disabled={!canEdit}
                  >
                    <MenuItem value="DIAMOND_POINT">💎 Diamond Point</MenuItem>
                    {enabledMetals
                      .filter((m) => m.status === true)
                      .map((m) => (
                        <MenuItem key={m._id} value={m._id}>
                          {`${m.metalName} - ${m.karat}K - ${m.purity}`}
                        </MenuItem>
                      ))}
                  </TextField>
                </Grid>
              )}

            {/* Reward Metal for REGISTRATION_GIFT — direct, no buyMetal condition */}
            {formData1.referralType === 'CustomerToCustomer' &&
              formData1.transactionsFrequency === 'REGISTRATION_GIFT' && (
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Reward Metal"
                    fullWidth
                    size="small"
                    required
                    value={formData1.metal || ''}
                    onChange={(e) => handleChange('metal', e.target.value)}
                    InputLabelProps={{ shrink: !!formData1.metal }}
                    sx={{ minWidth: 200 }}
                    disabled={!canEdit}
                  >
                    {enabledMetals
                      .filter((m) => m.status === true)
                      .map((m) => (
                        <MenuItem key={m._id} value={m._id}>
                          {`${m.metalName} - ${m.karat}K - ${m.purity}`}
                        </MenuItem>
                      ))}
                  </TextField>
                </Grid>
              )}

            {/* Normal reward: percentage (only when not diamond point, not registration) */}
            {formData1.referralType === 'CustomerToCustomer' &&
              formData1.transactionsFrequency !== 'REGISTRATION_GIFT' &&
              !formData1.isDiamondPoint && (
                <Grid item xs={12} md={6}>
                  <TextField
                    size="small"
                    type="number"
                    label="Percentage (%)"
                    fullWidth
                    value={formData1.percentage}
                    onChange={(e) => handleChange('percentage', e.target.value)}
                    disabled={!canEdit}
                    sx={{ minWidth: 200 }}
                  />
                </Grid>
              )}

            {/* Diamond Point: type + value */}
            {formData1.isDiamondPoint && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    label="Diamond Point Type"
                    fullWidth
                    size="small"
                    required
                    value={formData1.diamondPointType || ''}
                    onChange={(e) =>
                      handleChange('diamondPointType', e.target.value)
                    }
                    InputLabelProps={{ shrink: !!formData1.diamondPointType }}
                    sx={{ minWidth: 200 }}
                    disabled={!canEdit}
                  >
                    <MenuItem value="FIXED">Fixed</MenuItem>
                    <MenuItem value="PERCENTAGE">Percentage</MenuItem>
                  </TextField>
                </Grid>

                {formData1.diamondPointType && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      size="small"
                      type="number"
                      label={
                        formData1.diamondPointType === 'PERCENTAGE'
                          ? 'Diamond Point (%)'
                          : 'Diamond Point (Fixed)'
                      }
                      fullWidth
                      value={formData1.diamondPointValue}
                      onChange={(e) =>
                        handleChange('diamondPointValue', e.target.value)
                      }
                      inputProps={
                        formData1.diamondPointType === 'PERCENTAGE'
                          ? { min: 0, max: 100 }
                          : { min: 0 }
                      }
                      disabled={!canEdit}
                      sx={{ minWidth: 200 }}
                    />
                  </Grid>
                )}
              </>
            )}

            {formData1.transactionsFrequency !== 'REGISTRATION_GIFT' && (
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="Transaction Type"
                  fullWidth
                  size="small"
                  value={formData1.transactionsType || ''}
                  onChange={(e) =>
                    handleChange('transactionsType', e.target.value)
                  }
                  InputLabelProps={{
                    shrink: !!formData1.transactionsType,
                  }}
                  disabled={!canEdit}
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Sip">Sip</MenuItem>
                  <MenuItem value="Buy">Buy</MenuItem>
                </TextField>
              </Grid>
            )}

            {formData1.referralType === 'UserToCustomer' && (
              <Grid item xs={12} md={6}>
                <TextField
                  size="small"
                  type="number"
                  label="Points"
                  fullWidth
                  value={formData1.points}
                  onChange={(e) => handleChange('points', e.target.value)}
                  disabled={!canEdit}
                  sx={{ minWidth: 200 }}
                />
              </Grid>
            )}

            {formData1.referralType === 'CustomerToCustomer' &&
              formData1.transactionsFrequency === 'REGISTRATION_GIFT' && (
                <Grid item xs={12} md={6}>
                  <TextField
                    size="small"
                    type="number"
                    label="Weight"
                    fullWidth
                    value={formData1.pointsPerGram}
                    onChange={(e) =>
                      handleChange('pointsPerGram', e.target.value)
                    }
                    disabled={!canEdit}
                    sx={{ minWidth: 200 }}
                  />
                </Grid>
              )}

            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Start Date"
                  value={formData1.startDate}
                  onChange={(date) => handleChange('startDate', date)}
                  disabled={!canEdit}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      disabled: !canEdit,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="End Date"
                  value={formData1.endDate}
                  onChange={(date) => handleChange('endDate', date)}
                  disabled={!canEdit}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      disabled: !canEdit,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                size="small"
                type="number"
                label="Validity Days"
                fullWidth
                value={formData1.validityDays}
                onChange={(e) => handleChange('validityDays', e.target.value)}
                inputProps={{ min: 1 }}
                disabled={!canEdit}
                sx={{ minWidth: 200 }}
              />
            </Grid>

            {formData1.transactionsFrequency === 'REGISTRATION_GIFT' && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: 'block',
                    ml: 2,
                    width:
                      formData1.referralType === 'UserToCustomer'
                        ? '110%'
                        : '110%',
                    mt: formData1.referralType === 'CustomerToCustomer' ? 0 : 1,
                    mb: 2,
                  }}
                >
                  <Paper
                    elevation={12}
                    sx={{
                      p: 3,
                      mt: 0,
                      mb: 4,
                      borderRadius: 2,
                      boxShadow: 3,
                    }}
                  >
                    <Typography variant="h6" gutterBottom>
                      Joiner Benefits
                    </Typography>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          select
                          label="Select Metal"
                          fullWidth
                          size="small"
                          required
                          value={formData1.joinerBullion || ''}
                          onChange={(e) =>
                            handleChange('joinerBullion', e.target.value)
                          }
                          InputLabelProps={{
                            shrink: !!formData1.joinerBullion,
                          }}
                          disabled={!canEdit}
                          sx={{ minWidth: 200 }}
                        >
                          {enabledMetals
                            .filter((metal) => metal.status === true)
                            .map((metal) => (
                              <MenuItem key={metal._id} value={metal._id}>
                                {`${metal.metalName} - ${metal.karat}K - ${metal.purity}`}
                              </MenuItem>
                            ))}
                        </TextField>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          size="small"
                          type="number"
                          label="Weight"
                          fullWidth
                          value={formData1.joinerPointsPerGram || ''}
                          onChange={(e) =>
                            handleChange('joinerPointsPerGram', e.target.value)
                          }
                          disabled={!canEdit}
                          sx={{ minWidth: 200 }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  ml:
                    formData1.transactionsFrequency === 'REGISTRATION_GIFT'
                      ? formData1.referralType === 'CustomerToCustomer'
                        ? 63
                        : formData1.referralType === 'UserToCustomer'
                          ? 55
                          : 0
                      : 0,
                  mt:
                    formData1.transactionsFrequency === 'REGISTRATION_GIFT'
                      ? formData1.referralType === 'CustomerToCustomer'
                        ? 0
                        : formData1.referralType === 'UserToCustomer'
                          ? 0
                          : 0
                      : 0,
                  mb: 2,
                }}
              >
                {(canCreate || canEdit) && (
                  <Button
                    variant="contained"
                    sx={{
                      bgcolor: '#752ADA',
                      px: 6,
                      textTransform: 'none',
                    }}
                    onClick={handleSubmit1}
                    disabled={loadingBenfits}
                  >
                    {loadingBenfits ? 'Saving...' : 'Save'}
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
          <ReferralOrdersTable />
        </Paper>
      </Box>
    </AccessControl>
  );
};

export default ReferralBenefits;
